// Role-based permissions configuration
// Defines what each role can see and do in the application

import { UserRole } from "../stores/auth-store";

// Permission categories
export type PermissionCategory =
  | 'dashboard'
  | 'loads'
  | 'dispatch'
  | 'drivers'
  | 'fleet'
  | 'customers'
  | 'carriers'
  | 'invoices'
  | 'settlements'
  | 'compliance'
  | 'documents'
  | 'reports'
  | 'analytics'
  | 'settings'
  | 'messaging'
  | 'tracking'
  | 'marketplace'
  | 'integrations'
  | 'ifta'
  | 'collections'
  | 'lanes'
  | 'margins'
  | 'edi'
  | 'portal'
  | 'inspections'
  | 'maintenance'
  | 'workOrder'
  | 'expenses';

// Permission actions
export type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'export' | 'manage';

// Data visibility restrictions
export type DataVisibility = {
  revenue: boolean;
  costs: boolean;
  margins: boolean;
  settlements: boolean;
  customerRates: boolean;
  driverPay: boolean;
  companyFinancials: boolean;
};

// Navigation item visibility
export interface NavPermission {
  visible: boolean;
  readonly?: boolean;
}

// Role configuration
export interface RoleConfig {
  name: string;
  description: string;
  // Which nav sections are visible
  navigation: Partial<Record<PermissionCategory, NavPermission>>;
  // What data can be seen
  dataVisibility: DataVisibility;
  // Can manage billing/subscription
  canManageBilling: boolean;
  // Can invite/manage users
  canManageUsers: boolean;
  // Can access settings
  canAccessSettings: boolean;
  // Is this an admin role
  isAdmin: boolean;
}

// Role configurations
export const ROLE_CONFIGS: Record<UserRole, RoleConfig> = {
  admin: {
    name: 'Administrator',
    description: 'Full access to all features and settings',
    navigation: {
      dashboard: { visible: true },
      loads: { visible: true },
      dispatch: { visible: true },
      drivers: { visible: true },
      fleet: { visible: true },
      customers: { visible: true },
      carriers: { visible: true },
      invoices: { visible: true },
      settlements: { visible: true },
      compliance: { visible: true },
      documents: { visible: true },
      reports: { visible: true },
      analytics: { visible: true },
      settings: { visible: true },
      messaging: { visible: true },
      tracking: { visible: true },
      marketplace: { visible: true },
      integrations: { visible: true },
      ifta: { visible: true },
      collections: { visible: true },
      lanes: { visible: true },
      margins: { visible: true },
      edi: { visible: true },
      portal: { visible: true },
      inspections: { visible: true },
      maintenance: { visible: true },
      workOrder: { visible: true },
      expenses: { visible: true }
    },
    dataVisibility: {
      revenue: true,
      costs: true,
      margins: true,
      settlements: true,
      customerRates: true,
      driverPay: true,
      companyFinancials: true,
    },
    canManageBilling: true,
    canManageUsers: true,
    canAccessSettings: true,
    isAdmin: true,
  },

  dispatcher: {
    name: 'Dispatcher',
    description: 'Manages loads and driver assignments',
    navigation: {
      dashboard: { visible: true },
      loads: { visible: true },
      dispatch: { visible: true },
      drivers: { visible: true, readonly: true },
      fleet: { visible: true, readonly: true },
      customers: { visible: true, readonly: true },
      documents: { visible: true },
      messaging: { visible: true },
      tracking: { visible: true },
      inspections: { visible: true },
      maintenance: { visible: false },
      workOrder: { visible: false },
      expenses: { visible: false }
    },
    dataVisibility: {
      revenue: true,
      costs: false,
      margins: false,
      settlements: false,
      customerRates: true,
      driverPay: false,
      companyFinancials: false,
    },
    canManageBilling: false,
    canManageUsers: false,
    canAccessSettings: false,
    isAdmin: false,
  },

  driver: {
    name: 'Driver',
    description: 'Views assigned loads and updates status',
    navigation: {
      dashboard: { visible: true },
      loads: { visible: true, readonly: true },
      documents: { visible: true },
      messaging: { visible: true },
      settlements: { visible: true, readonly: true }, // Own settlements only
      inspections: { visible: true },
      maintenance: { visible: false },
      workOrder: { visible: false },
      expenses: { visible: false }
    },
    dataVisibility: {
      revenue: false,
      costs: false,
      margins: false,
      settlements: true, // Own only
      customerRates: false,
      driverPay: true, // Own only
      companyFinancials: false,
    },
    canManageBilling: false,
    canManageUsers: false,
    canAccessSettings: false,
    isAdmin: false,
  },

  accountant: {
    name: 'Accountant',
    description: 'Manages invoices, settlements, and finances',
    navigation: {
      dashboard: { visible: true },
      loads: { visible: true, readonly: true },
      dispatch: { visible: false },
      drivers: { visible: false },
      fleet: { visible: false },
      customers: { visible: false },
      carriers: { visible: false },
      invoices: { visible: true },
      settlements: { visible: true },
      compliance: { visible: false },
      documents: { visible: true },
      reports: { visible: true },
      analytics: { visible: true },
      messaging: { visible: false },
      tracking: { visible: false },
      integrations: { visible: false },
      ifta: { visible: true },
      collections: { visible: true },
      lanes: { visible: false },
      margins: { visible: false },
      edi: { visible: false },
      inspections: { visible: false },
      marketplace: { visible: false },
      settings: { visible: false },
      portal: { visible: false },
      maintenance: { visible: false },
      workOrder: { visible: false },
      expenses: { visible: false }
    },
    dataVisibility: {
      revenue: true,
      costs: true,
      margins: true,
      settlements: true,
      customerRates: true,
      driverPay: true,
      companyFinancials: true,
    },
    canManageBilling: false,
    canManageUsers: false,
    canAccessSettings: false,
    isAdmin: false,
  },

  safety_manager: {
    name: 'Safety Manager',
    description: 'Manages compliance and safety records',
    navigation: {
      dashboard: { visible: true },
      drivers: { visible: true },
      fleet: { visible: true },
      compliance: { visible: true },
      documents: { visible: true },
      reports: { visible: true },
      inspections: { visible: true },
      maintenance: { visible: true },
      workOrder: { visible: true },
    },
    dataVisibility: {
      revenue: false,
      costs: false,
      margins: false,
      settlements: false,
      customerRates: false,
      driverPay: false,
      companyFinancials: false,
    },
    canManageBilling: false,
    canManageUsers: false,
    canAccessSettings: false,
    isAdmin: false,
  },

  broker_agent: {
    name: 'Broker Agent',
    description: 'Books loads and manages carrier relationships',
    navigation: {
      dashboard: { visible: true },
      loads: { visible: true },
      dispatch: { visible: true },
      carriers: { visible: true },
      customers: { visible: true },
      lanes: { visible: true },
      marketplace: { visible: true },
      documents: { visible: true },
      messaging: { visible: true },
      tracking: { visible: true },
      maintenance: { visible: false },
      workOrder: { visible: false },
    },
    dataVisibility: {
      revenue: true,
      costs: true,
      margins: true,
      settlements: false,
      customerRates: true,
      driverPay: false,
      companyFinancials: false,
    },
    canManageBilling: false,
    canManageUsers: false,
    canAccessSettings: false,
    isAdmin: false,
  },

  customer_admin: {
    name: 'Customer Admin',
    description: 'Full access to customer portal',
    navigation: {
      dashboard: { visible: true },
      loads: { visible: true, readonly: true },
      tracking: { visible: true },
      invoices: { visible: true, readonly: true },
      documents: { visible: true, readonly: true },
      messaging: { visible: true },
      maintenance: { visible: false },
      workOrder: { visible: false },
    },
    dataVisibility: {
      revenue: false,
      costs: false,
      margins: false,
      settlements: false,
      customerRates: false,
      driverPay: false,
      companyFinancials: false,
    },
    canManageBilling: false,
    canManageUsers: true, // Can manage their own portal users
    canAccessSettings: false,
    isAdmin: false,
  },

  customer_viewer: {
    name: 'Customer Viewer',
    description: 'View-only access to shipment tracking',
    navigation: {
      dashboard: { visible: true },
      loads: { visible: true, readonly: true },
      tracking: { visible: true },
      documents: { visible: true, readonly: true },
      maintenance: { visible: false },
      workOrder: { visible: false },
    },
    dataVisibility: {
      revenue: false,
      costs: false,
      margins: false,
      settlements: false,
      customerRates: false,
      driverPay: false,
      companyFinancials: false,
    },
    canManageBilling: false,
    canManageUsers: false,
    canAccessSettings: false,
    isAdmin: false,
  },
};

const fullPermissionRole = {
  name: 'Administrator',
  description: 'Full access to all features and settings',
  navigation: {
    dashboard: { visible: true },
    loads: { visible: true },
    dispatch: { visible: true },
    drivers: { visible: true },
    fleet: { visible: true },
    customers: { visible: true },
    carriers: { visible: true },
    invoices: { visible: true },
    settlements: { visible: true },
    compliance: { visible: true },
    documents: { visible: true },
    reports: { visible: true },
    analytics: { visible: true },
    settings: { visible: true },
    messaging: { visible: true },
    tracking: { visible: true },
    marketplace: { visible: true },
    integrations: { visible: true },
    ifta: { visible: true },
    collections: { visible: true },
    lanes: { visible: true },
    margins: { visible: true },
    edi: { visible: true },
    portal: { visible: true },
    inspections: { visible: true },
    maintenance: { visible: true },
    workOrder: { visible: true },
  },
  dataVisibility: {
    revenue: true,
    costs: true,
    margins: true,
    settlements: true,
    customerRates: true,
    driverPay: true,
    companyFinancials: true,
  },
  canManageBilling: true,
  canManageUsers: true,
  canAccessSettings: true,
  isAdmin: true,
};

// Helper functions
export function getRoleConfig(role: UserRole): RoleConfig {
  return ROLE_CONFIGS[role] || fullPermissionRole;
}

export function canViewNavItem(role: UserRole, category: PermissionCategory): boolean {
  const config = getRoleConfig(role);
  return config.navigation[category]?.visible ?? false;
}

export function isNavItemReadonly(role: UserRole, category: PermissionCategory): boolean {
  const config = getRoleConfig(role);
  return config.navigation[category]?.readonly ?? false;
}

export function canViewData(role: UserRole, dataType: keyof DataVisibility): boolean {
  const config = getRoleConfig(role);
  return config.dataVisibility[dataType];
}

export function isAdmin(role: UserRole): boolean {
  return getRoleConfig(role).isAdmin;
}

export function canManageBilling(role: UserRole): boolean {
  return getRoleConfig(role).canManageBilling;
}

export function canManageUsers(role: UserRole): boolean {
  return getRoleConfig(role).canManageUsers;
}

export function canAccessSettings(role: UserRole): boolean {
  return getRoleConfig(role).canAccessSettings;
}
